import { Colors, Lightning, Storage } from "@lightningjs/sdk";
import { SettingsItemText } from "../layout/SettingsItemText";
import QRCode from "qrcode";
import configs from "../../../../../lib/configs";
import theme from "../../../../../lib/theme";
import { STORAGE_KEYS } from "../../../../../lib/utils";
import { fetchAndCacheAppInfo, type AppInfo } from "../../../../../api/queries";

interface SettingsTermsTemplateSpec extends Lightning.Component.TemplateSpec {
    Layout: typeof SettingsItemText;
}

const TEXT_VIEWPORT_W = 700;
const TEXT_VIEWPORT_H = 900;
const SCROLL_STEP = 60;

function htmlToPlainText(html: string): string {
    return html
        .replace(/<\s*\/?(p|div|br|li|h[1-6])[^>]*>/gi, "\n")
        .replace(/<[^>]+>/g, "")
        .replace(/&nbsp;/g, " ")
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
}

export class SettingsTermsContent
    extends Lightning.Component<any>
    implements Lightning.Component.ImplementTemplateSpec<any>
{
    readonly QrFallback = this.getByRef("QrFallback")!;
    readonly QR = this.QrFallback.getByRef("QR")!;
    readonly QrText = this.QrFallback.getByRef("QrText")!;
    readonly CmsView = this.getByRef("CmsView")!;
    readonly Scroller = this.CmsView.getByRef("Scroller")!;
    readonly Body = this.Scroller.getByRef("Body")!;

    private _scrollY = 0;
    private _maxScroll = 0;
    private _hasCmsText = false;

    static override _template(): Lightning.Component.Template<any> {
        return {
            w: TEXT_VIEWPORT_W,
            CmsView: {
                alpha: 0,
                w: TEXT_VIEWPORT_W,
                Scroller: {
                    w: TEXT_VIEWPORT_W,
                    h: TEXT_VIEWPORT_H,
                    clipping: true,
                    Body: {
                        y: 0,
                        text: {
                            text: "",
                            textAlign: "left",
                            textColor: Colors(theme.colors.text.secondary).get(),
                            fontSize: 22,
                            lineHeight: 32,
                            wordWrap: true,
                            wordWrapWidth: TEXT_VIEWPORT_W - 20
                        }
                    }
                }
            },
            QrFallback: {
                alpha: 0,
                flex: { direction: "column", alignItems: "center", justifyContent: "center" },
                w: 540,
                QR: { w: 540, h: 540 },
                QrText: {
                    y: 24,
                    width: 340,
                    text: {
                        text: "",
                        textAlign: "center",
                        textWidth: 340,
                        textColor: Colors(theme.colors.text.secondary).get(),
                        fontSize: 28,
                        wrap: true,
                        wrapWidth: 800
                    }
                }
            }
        };
    }

    override _init() {
        void this._refresh();
    }

    override _active() {
        void this._refresh();
    }

    get hasCmsText(): boolean {
        return this._hasCmsText;
    }

    private async _refresh() {
        await this._render();
        const fresh = await fetchAndCacheAppInfo();
        if (fresh) await this._render();
    }

    private async _render() {
        const appInfo = Storage.get(STORAGE_KEYS.appInfo) as AppInfo | undefined;
        const raw = appInfo?.termsAndConditions?.contentText ?? null;
        const plain = raw ? htmlToPlainText(raw) : "";
        this._hasCmsText = plain.length > 0;

        if (this._hasCmsText) {
            this.CmsView.patch({ alpha: 1 });
            this.QrFallback.patch({ alpha: 0 });
            this.Body.patch({ y: 0, text: { text: plain } });
            this._scrollY = 0;
            // Compute max scroll once layout is done.
            setTimeout(() => {
                const bodyH = (this.Body as any).renderHeight || 0;
                this._maxScroll = Math.max(0, Math.ceil(bodyH) - TEXT_VIEWPORT_H);
            }, 100);
        } else {
            this.CmsView.patch({ alpha: 0 });
            this.QrFallback.patch({ alpha: 1 });
            const url = configs.settings.terms || "";
            this.QrText.patch({ text: { text: url } });
            if (url) {
                try {
                    this.QR.patch({
                        src: await QRCode.toDataURL(url, {
                            errorCorrectionLevel: "H",
                            type: "image/jpeg"
                        })
                    });
                } catch (err) {
                    console.warn("Terms QR encode failed:", err);
                }
            }
        }
    }

    /** Called by the parent when scrolling is active. Always consumes (stays in scroll mode at boundaries). */
    _handleUp(): boolean {
        if (!this._hasCmsText) return false;
        if (this._scrollY > 0) {
            this._scrollY = Math.max(0, this._scrollY - SCROLL_STEP);
            this.Body.patch({ smooth: { y: -this._scrollY } });
        }
        return true;
    }

    _handleDown(): boolean {
        if (!this._hasCmsText) return false;
        if (this._scrollY < this._maxScroll) {
            this._scrollY = Math.min(this._maxScroll, this._scrollY + SCROLL_STEP);
            this.Body.patch({ smooth: { y: -this._scrollY } });
        }
        return true;
    }
}

export class SettingsTerms
    extends Lightning.Component<SettingsTermsTemplateSpec>
    implements Lightning.Component.ImplementTemplateSpec<SettingsTermsTemplateSpec>
{
    readonly SettingsItem = this.getByRef("Layout")!;

    /** When true, focus is on the inner scroll content; up/down scroll, left exits. */
    private _scrollMode = false;

    static override _template(): Lightning.Component.Template<SettingsTermsTemplateSpec> {
        return {
            Layout: {
                type: SettingsItemText,
                title: "about.terms",
                content: SettingsTermsContent
            }
        };
    }

    override _getFocused() {
        if (this._scrollMode) {
            const content = this._content();
            if (content) return content;
        }
        return this.SettingsItem;
    }

    override _handleEnter() {
        // Same as Right: enter scroll mode if there's scrollable content.
        this._enterScrollMode();
    }

    override _handleRight() {
        return this._enterScrollMode();
    }

    override _handleLeft() {
        if (this._scrollMode) {
            this._scrollMode = false;
            this._refocus();
            return true;
        }
        return false;
    }

    private _enterScrollMode(): boolean | void {
        const content = this._content();
        if (content?.hasCmsText) {
            this._scrollMode = true;
            this._refocus();
            return true;
        }
        return false;
    }

    private _content(): SettingsTermsContent | null {
        const items = (this.SettingsItem as any).CustomContent?.children ?? [];
        return (items[0] as SettingsTermsContent) ?? null;
    }

    private _refocus() {
        // Force Lightning to re-resolve _getFocused on next tick.
        (this.application as any)._refocus?.();
    }

    _handleClick() {
        this._handleEnter();
    }

    _handleHover() {
        this.fireAncestors("$listItemHover", this);
    }
}
